import React, { useEffect, useState, useMemo } from "react";

import useDebounce from "hooks/useDebounce";
import useGlobalSearch from "hooks/useGlobalSearch";
import { generateCancelToken } from "interceptor/aeh";
import { API_FETCH_STATUS } from "constants/common";
import Tooltip from "components/tooltip";

import { SEARCH_TYPES, TITLE_MAX_LENGTH, TYPE_AHEAD_LIMIT } from "../constants";
import { faOnLoadMoreClick, faOnNoSearchResult } from "../services/analytics";
import styles from "./typeAheadSearch.scss";

const {
    type_ahead_search,
    results,
    results_found,
    load_more,
    did_not_found,
    not_found_text,
    search_keyword,
    keyword_search_icon,
    disabled,
    search_tag,
    search_message,
    search_query,
    search_icon,
    list_container,
    search_list,
    search_text,
    search_content,
    ellipsis,
    module_text
} = styles;

const { SUCCESS } = API_FETCH_STATUS;


export default function TypeAheadSearch({ searchQuery, handleSearchSelect }) {

    const [displayCount, setDisplayCount] = useState(TYPE_AHEAD_LIMIT / 2);

    const { fetchTypeAheadSearch, typeAheadSearch, setTypeAheadSearch } = useGlobalSearch();
    const displayTypeAheadSearch = useMemo(() => typeAheadSearch.contents ? typeAheadSearch.contents.slice(0, displayCount) : [], [displayCount, typeAheadSearch]);
    const debouncedSearchQuery = useDebounce(searchQuery, 400);

    useEffect(() => {
        const cancelTokenSource = generateCancelToken();
        fetchTypeAheadSearch(debouncedSearchQuery, cancelTokenSource);
        setDisplayCount(TYPE_AHEAD_LIMIT / 2);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [debouncedSearchQuery]);

    useEffect(() => {
       if (typeAheadSearch.contents?.length === 0) {
        faOnNoSearchResult({ searchQuery });
       }
    }, [typeAheadSearch]);

    const loadMore = () => {
        faOnLoadMoreClick({ searchQuery });
        setDisplayCount(TYPE_AHEAD_LIMIT);
    };

    if (typeAheadSearch.state === SUCCESS && displayTypeAheadSearch.length === 0) {
        return (
            <div className={type_ahead_search}>
                <div className={results}>
                    <p className={results_found}>No results found for <span className={search_query}>"{searchQuery}"</span></p>
                    <p className={search_message}>Try shortening or rephrasing your search.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={type_ahead_search}>
            <div className={list_container}>
                <ul className={search_list}>
                    {displayTypeAheadSearch.map(search => {
                        const { id, title, module, metaDataMap: { tag }, handlerId } = search;
                        return (
                            <li key={id} className={tag ? styles[tag.type.toLowerCase()] : ""}>
                                <a href={handlerId} role="button" onClick={(event) => handleSearchSelect({ event, type: SEARCH_TYPES.TYPE_AHEAD, search })}>
                                    <span className={search_icon}>
                                        {handlerId ? <i className="aeh-icon-outlined-arrow-right"></i> : <i className="aeh-icon-hourglass"></i>}
                                    </span>
                                    <div className={search_content}>
                                        {title.length > TITLE_MAX_LENGTH ? <Tooltip
                                            parent={<p className={`${search_text} ${ellipsis}`}>{title}</p>}
                                            content={title}
                                        /> : <p className={`${search_text} ${ellipsis}`}>{title}</p>}
                                        <div className={module_text}>in {module}</div>
                                    </div>
                                    {tag && <div className={`${search_tag} ${styles[tag.type.toLowerCase()]}`}>{tag.value}</div>}
                                </a>
                            </li>
                        )
                    })}
                </ul>
            </div>
            {typeAheadSearch.state === SUCCESS && displayCount < Math.min(TYPE_AHEAD_LIMIT, typeAheadSearch.contents?.length) && <div className={load_more} onClick={loadMore}>Load More Results</div>}
            {typeAheadSearch.state === SUCCESS && displayCount === TYPE_AHEAD_LIMIT && (
               <div className={did_not_found}>
               <div className={not_found_text}>
                    <p>did not find what you are looking for?</p>
                    <div className={search_keyword}>
                        search for another keyword 
                        <span className={keyword_search_icon}>
                            <i className="aeh-icon-outlined-search-icon"></i>
                        </span>
                    </div>
               </div>
           </div>
            )} 
        </div>
    )
}
